import React, { FC, useCallback, useState, useEffect, useRef } from 'react';
import NumberFormat from 'react-number-format';
import { addSeconds, isAfter } from 'date-fns';

import capitalizeFirstLetter from 'utils/CapitalizeFirstLetter';
import ToggleSwitcher from 'components/ToggleSwitcher';
import ConfirmModal from 'components/ConfirmModal';

import trashIcon from 'assets/img/trash-icon.svg';
import lessIcon from 'assets/img/less-icon.svg';
import plusIcon from 'assets/img/plus-icon.svg';

import { CourseTableListDTO, useAutoApproval } from 'hooks/useAutoApproval';
import { faPlusSquare } from '@fortawesome/free-solid-svg-icons';
import {
  Container,
  CourseInfo,
  CreditPorcentageWrapper,
  NumberFormatInput,
} from './styles';

interface TableItemProps {
  course: CourseTableListDTO;
  index: number;
  handleFormData: (params: HandleFormDataParams) => void;
  setNeedFillSomeInput: React.Dispatch<React.SetStateAction<boolean>>;
  setRemoveSelectedList: React.Dispatch<React.SetStateAction<any[]>>;
  headerChecked: boolean;
  removeSelectedList: any[];
}

export interface TableItemDTO {
  isChecked: boolean;
  hasPlacesLimit: boolean;
  placesQtd: string;
  creditPercentage: number;
  monthlyValue: number | unknown;
}

export interface HandleFormDataParams {
  course?: CourseTableListDTO;
  type?: 'toggleSwitch';
  index: number;
  currentData: TableItemDTO;
}

const TableItem: FC<TableItemProps> = ({
  course,
  index,
  handleFormData,
  setNeedFillSomeInput,
  headerChecked,
  setRemoveSelectedList,
  removeSelectedList,
}) => {
  const [isChecked, setIsChecked] = useState<boolean>(false);
  const [hasPlacesLimit, setHasPlacesLimit] = useState<boolean>(
    course.limiteVagas === 'S',
  );
  const [placesQtd, setPlacesQtd] = useState(Number(course.vagas) || '0');
  const [creditPercentageValue, setCreditPercentageValue] = useState(
    course.percentual,
  );
  const [monthlyValue, setMonthlyValue] = useState<number | unknown>(
    course.mensalidade,
  );
  const creditPercentangeInputRef = useRef<NumberFormat>();
  const [showDialog, toggleDialog] = useState(false);
  const [lastUpdateDate, setLastUpdateDate] = useState<Date>(new Date());

  const { removeTableItem, editCourseData } = useAutoApproval();
  const [showItem, toggleItem] = useState(true);

  useEffect(() => {
    if (headerChecked) {
      setIsChecked(true);
      setRemoveSelectedList((currState) => [...currState, course]);
    } else {
      setIsChecked(false);
      setRemoveSelectedList([]);
    }
  }, [headerChecked]);

  useEffect(() => {
    if (removeSelectedList.length === 0) {
      setIsChecked(false);
    }
  }, [removeSelectedList]);

  useEffect(() => {
    if (isChecked) {
      setRemoveSelectedList((currState) => [...currState, course]);
    } else {
      setRemoveSelectedList([]);
    }
  }, [isChecked]);

  useEffect(() => {
    if (hasPlacesLimit && Number(placesQtd) === 0) {
      setNeedFillSomeInput(true);
    } else {
      setNeedFillSomeInput(false);
    }
  }, [hasPlacesLimit, placesQtd]);

  useEffect(() => {
    const interval = setInterval(() => {
      if ((hasPlacesLimit && Number(placesQtd) > 0) || !hasPlacesLimit) {
        if (isAfter(Date.now(), addSeconds(lastUpdateDate, 3))) {
          editCourseData({
            id: course.id,
            limiteVagas: hasPlacesLimit ? 'S' : 'N',
            vagas: Number(placesQtd),
            percentual: creditPercentageValue,
            mensalidade: Number(monthlyValue),
          });

          setLastUpdateDate(new Date());
          clearInterval(interval);
        }
      } else {
        alert('Preencha o numero de vagas');
        clearInterval(interval);
      }
    }, 3000);

    return () => {
      clearInterval(interval);
    };
  }, [
    isChecked,
    hasPlacesLimit,
    placesQtd,
    creditPercentageValue,
    monthlyValue,
  ]);

  interface HandleCreditPctProps {
    value: string | number;
    isFromInput?: boolean;
  }

  const handlePlacesQtd = useCallback(
    (value: string) => {
      const regex = /^[0-9]*$/g;

      if (!regex.test(value) || Number(value) >= 299) {
        return;
      }

      setPlacesQtd(value);
    },
    [placesQtd],
  );

  const handleCreditPercentage = useCallback(
    ({ value, isFromInput = false }: HandleCreditPctProps) => {
      if (isFromInput) {
        const regex = /^[0-9]*$/g;

        if (
          !regex.test(value.toString()) ||
          Number(value) >= 101 ||
          Number(value < 0)
        ) {
          return;
        }

        setCreditPercentageValue(Number(value));
      }

      if (typeof value === 'number') {
        setCreditPercentageValue((currValue) => {
          if (currValue + value > 100) return 100;
          if (currValue + value < 0) return 0;

          return currValue + value;
        });
      }
    },
    [],
  );

  return (
    <>
      {showDialog && (
        <ConfirmModal
          title="Deseja remover esse curso das sua aprovação automática?"
          handleOnReject={() => toggleDialog(false)}
          handleOnAccept={() => {
            removeTableItem(course.id);
            toggleItem(false);
            toggleDialog(false);
          }}
        />
      )}
      {showItem ? (
        <Container>
          <CourseInfo>
            <input
              type="checkbox"
              id={`course-id-${course.id}`}
              checked={isChecked}
              onChange={(): void => setIsChecked(!isChecked)}
            />
            <label htmlFor={`course-id-${course.id}`}>
              {capitalizeFirstLetter(course.nome || '-')}
            </label>
            <span>{capitalizeFirstLetter(course.turno || '-')}</span>
          </CourseInfo>
          <span>{course.campus || 'Campus I'}</span>
          <ToggleSwitcher
            setHasPlacesLimit={setHasPlacesLimit}
            defaultValue={hasPlacesLimit}
          />
          <input
            disabled={!hasPlacesLimit}
            type="text"
            value={hasPlacesLimit ? placesQtd : 'Sem limite'}
            className="input-vacancies-qtd"
            onChange={({ target }) => handlePlacesQtd(target.value)}
          />
          <CreditPorcentageWrapper>
            <img
              role="presentation"
              alt="Diminuir porcentagem"
              src={lessIcon}
              onClick={() => handleCreditPercentage({ value: -1 })}
            />
            <NumberFormatInput
              value={creditPercentageValue}
              suffix=" %"
              allowNegative={false}
              isAllowed={(values) =>
                values.floatValue >= 0 && values.floatValue <= 100
              }
              ref={creditPercentangeInputRef}
              onBlur={(): void => {
                if (creditPercentangeInputRef.current) {
                  handleCreditPercentage({
                    isFromInput: true,
                    value: creditPercentangeInputRef.current.state.numAsString,
                  });
                }
              }}
            />
            <img
              role="presentation"
              alt="Aumentar porcentagem"
              src={plusIcon}
              onClick={() => handleCreditPercentage({ value: 1 })}
            />
          </CreditPorcentageWrapper>
          <NumberFormatInput
            displayType="text"
            value={monthlyValue}
            prefix="R$ "
            allowNegative={false}
            fixedDecimalScale
            decimalScale={2}
            decimalSeparator=","
            thousandSeparator="."
            onValueChange={(values) => setMonthlyValue(values.floatValue)}
            placeholder="R$ 0,00"
          />
          <span>06</span>
          <span
            className="trash-icon"
            role="presentation"
            onClick={() => toggleDialog(true)}
          >
            <img src={trashIcon} alt="Apagar curso" />
            Apagar
          </span>
        </Container>
      ) : (
        <></>
      )}
    </>
  );
};

export default TableItem;
